import React, { useState } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { registerGuardian } from '../../lib/api';

/**
 * JoinGuardianModal — the email-capture moment for the Vedic Biophysics
 * banner CTA. A small, sovereign-feeling overlay on Miron Violet that
 * registers the guest with /api/guardians and returns their Guardian
 * number (sequential, immutable) along with the founding-cap progress.
 *
 * Closes via × button, backdrop click, or Escape.
 */

const JoinGuardianModal = ({ open, onClose, testId = 'join-guardian-modal', source = 'home_banner' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');  // idle · sending · ok · error
  const [error, setError]   = useState('');
  const [result, setResult] = useState(null);

  const reset = () => {
    setEmail(''); setStatus('idle'); setError(''); setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose && onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setStatus('sending'); setError('');
    try {
      const data = await registerGuardian(email.trim().toLowerCase(), source);
      setResult(data);
      setStatus('ok');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not register — please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not register — please try again.');
      setStatus('error');
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-label="Join the Circle of Guardians"
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-10"
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        data-testid={`${testId}-backdrop`}
        onClick={handleClose}
        className="absolute inset-0 bg-[#0E0824]/85 backdrop-blur-md"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-[560px] overflow-hidden border border-[#D4AF5A]/60 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)]"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, #2E1A4A 0%, #1E1230 60%, #150A22 100%)',
        }}
      >
        {/* Gold top hairline */}
        <span
          aria-hidden
          className="block w-full h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(212,175,90,0.7) 35%, rgba(212,175,90,0.7) 65%, transparent)',
          }}
        />

        <button
          type="button"
          onClick={handleClose}
          data-testid={`${testId}-close`}
          aria-label="Close"
          className="absolute top-4 right-4 text-[#F4F8F9]/55 hover:text-[#D4AF5A] transition-colors"
        >
          <X size={20} strokeWidth={1.6} />
        </button>

        <div className="relative px-8 md:px-12 pt-12 pb-10 text-center">
          {status !== 'ok' && (
            <>
              <p
                data-testid={`${testId}-overline`}
                className="font-mono text-[10px] tracking-[0.42em] uppercase font-semibold text-[#D4AF5A] mb-5 flex items-center justify-center gap-3"
              >
                <span aria-hidden className="inline-block w-7 h-px bg-[#D4AF5A]" />
                The Founding Ledger · § Circle
                <span aria-hidden className="inline-block w-7 h-px bg-[#D4AF5A]" />
              </p>

              <h2
                data-testid={`${testId}-title`}
                className="font-serif text-[#D4AF5A] font-light leading-[0.96] mb-5"
                style={{ fontSize: 'clamp(32px, 4.5vw, 56px)' }}
              >
                Join the Circle<br />of <em className="text-[#E8C977]">Guardians</em>.
              </h2>
              <p className="font-serif italic text-[#F4F8F9]/85 text-lg leading-snug max-w-md mx-auto mb-8">
                1,000 founding seats. Your number is sequential and yours
                alone. 2× SEVA earn, named on the bottle, witnessed on chain.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@your-atelier.com"
                  data-testid={`${testId}-email-input`}
                  autoFocus
                  className="w-full bg-[#F4F8F9]/[0.06] border border-[#F4F8F9]/25 focus:border-[#D4AF5A] focus:outline-none px-5 py-4 font-mono text-sm tracking-[0.06em] text-[#F4F8F9] placeholder:text-[#F4F8F9]/40"
                />
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid={`${testId}-submit`}
                  className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 font-mono text-[11px] tracking-[0.32em] uppercase font-semibold text-[#1A0F26] bg-[#D4AF5A] hover:bg-[#E8C977] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {status === 'sending' ? 'Inscribing…' : 'Inscribe my name'}
                  {status !== 'sending' && (
                    <ArrowRight size={13} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
                  )}
                </button>
              </form>
              {error && (
                <p
                  data-testid={`${testId}-error`}
                  className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#FFC8B4]"
                >
                  {error}
                </p>
              )}

              <p className="mt-6 font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/45">
                No marketing. The Circle is a witness, not a list.
              </p>
            </>
          )}

          {status === 'ok' && result && (
            <div data-testid={`${testId}-success`}>
              <CheckCircle2
                size={42}
                strokeWidth={1.3}
                className="mx-auto text-[#D4AF5A] mb-6"
              />
              <p className="font-mono text-[10px] tracking-[0.42em] uppercase font-semibold text-[#D4AF5A] mb-5">
                Inscribed
              </p>
              <h2
                className="font-serif text-[#D4AF5A] font-light leading-[0.96] mb-5"
                style={{ fontSize: 'clamp(32px, 4.5vw, 56px)' }}
              >
                Guardian
                <br />
                <em
                  className="text-[#E8C977]"
                  data-testid={`${testId}-guardian-number`}
                >
                  № {result.guardian_number ?? result.guardianNumber ?? '—'}
                </em>
              </h2>
              <p className="font-serif italic text-[#F4F8F9]/90 text-lg leading-snug max-w-md mx-auto mb-6">
                Your number is sequential, immutable, and yours alone.
                The bottle will carry it.
              </p>
              {(result.founding_cap || result.total_registered) && (
                <p
                  data-testid={`${testId}-progress`}
                  className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/65"
                >
                  {result.total_registered ?? '—'} / {result.founding_cap ?? 1000} inscribed ·{' '}
                  {Math.max(0, (result.founding_cap ?? 1000) - (result.total_registered ?? 0))} seats remain
                </p>
              )}
              <button
                type="button"
                onClick={handleClose}
                data-testid={`${testId}-success-close`}
                className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.32em] uppercase text-[#F4F8F9]/85 hover:text-[#D4AF5A] transition-colors"
              >
                Return to the still
              </button>
            </div>
          )}
        </div>

        {/* Gold bottom hairline */}
        <span
          aria-hidden
          className="block w-full h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(212,175,90,0.55) 35%, rgba(212,175,90,0.55) 65%, transparent)',
          }}
        />
      </div>
    </div>
  );
};

export default JoinGuardianModal;
